'use client';

import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import mergeRefs from 'merge-refs';
import invariant from 'tiny-invariant';
import warning from 'warning';
import * as pdfjs from 'pdfjs-dist';
import StructTree from '../StructTree.js';
import usePageContext from '../shared/hooks/usePageContext.js';
import { cancelRunningTask, getDevicePixelRatio, isCancelException, makePageCallback } from '../shared/utils.js';
var ANNOTATION_MODE = pdfjs.AnnotationMode;
export default function Canvas(props) {
  var pageContext = usePageContext();
  invariant(pageContext, 'Unable to find Page context.');
  var mergedProps = Object.assign(Object.assign({}, pageContext), props);
  var _className = mergedProps._className,
    canvasBackground = mergedProps.canvasBackground,
    _mergedProps$devicePi = mergedProps.devicePixelRatio,
    devicePixelRatio = _mergedProps$devicePi === void 0 ? getDevicePixelRatio() : _mergedProps$devicePi,
    onRenderErrorProps = mergedProps.onRenderError,
    onRenderSuccessProps = mergedProps.onRenderSuccess,
    page = mergedProps.page,
    renderForms = mergedProps.renderForms,
    renderTextLayer = mergedProps.renderTextLayer,
    rotate = mergedProps.rotate,
    scale = mergedProps.scale;
  var canvasRef = props.canvasRef;
  invariant(page, 'Attempted to render page canvas, but no page was specified.');
  var canvasElement = useRef(null);
  /**
   * Called when a page is rendered successfully.
   */
  function onRenderSuccess() {
    if (!page) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    if (onRenderSuccessProps) {
      onRenderSuccessProps(makePageCallback(page, scale));
    }
  }
  /**
   * Called when a page fails to render.
   */
  function onRenderError(error) {
    if (isCancelException(error)) {
      return;
    }
    warning(false, error.toString());
    if (onRenderErrorProps) {
      onRenderErrorProps(error);
    }
  }
  var renderViewport = useMemo(function () {
    return page.getViewport({
      scale: scale * devicePixelRatio,
      rotation: rotate
    });
  }, [devicePixelRatio, page, rotate, scale]);
  var viewport = useMemo(function () {
    return page.getViewport({
      scale: scale,
      rotation: rotate
    });
  }, [page, rotate, scale]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Ommitted callbacks so they are not called every time they change
  useEffect(function drawPageOnCanvas() {
    if (!page) {
      return;
    }
    // Ensures the canvas will be re-rendered from scratch. Otherwise all form data will stay.
    page.cleanup();
    var canvas = canvasElement.current;
    if (!canvas) {
      return;
    }
    canvas.width = renderViewport.width;
    canvas.height = renderViewport.height;
    canvas.style.width = "".concat(Math.floor(viewport.width), "px");
    canvas.style.height = "".concat(Math.floor(viewport.height), "px");
    canvas.style.visibility = 'hidden';
    var renderContext = {
      annotationMode: renderForms ? ANNOTATION_MODE.ENABLE_FORMS : ANNOTATION_MODE.ENABLE,
      canvasContext: canvas.getContext('2d', {
        alpha: false
      }),
      viewport: renderViewport
    };
    if (canvasBackground) {
      renderContext.background = canvasBackground;
    }
    var cancellable = page.render(renderContext);
    var runningTask = cancellable;
    cancellable.promise.then(function () {
      canvas.style.visibility = '';
      onRenderSuccess();
    }).catch(onRenderError);
    return function () {
      return cancelRunningTask(runningTask);
    };
  }, [canvasBackground, page, renderForms, renderViewport, viewport]);
  var cleanup = useCallback(function () {
    var canvas = canvasElement.current;
    /**
     * Zeroing the width and height cause most browsers to release graphics
     * resources immediately, which can greatly reduce memory consumption.
     */
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }, []);
  useEffect(function () {
    return cleanup;
  }, [cleanup]);
  return _jsx("canvas", {
    className: "".concat(_className, "__canvas"),
    dir: "ltr",
    ref: mergeRefs(canvasRef, canvasElement),
    style: {
      display: 'block',
      userSelect: 'none'
    },
    children: renderTextLayer ? _jsx(StructTree, {}) : null
  });
}